/**
 * Streaming Suspense - Condition-based suspension for streaming data.
 *
 * Components suspend while streaming AND data is incomplete (based on conditions).
 * When data meets conditions OR streaming ends, suspension resolves.
 *
 * IMPORTANT: The parent component MUST re-render when the value changes
 * for Suspense to resolve. This happens automatically when:
 * - Parent state changes (e.g., slides prop updates during streaming)
 * - Parent receives new props from its parent
 *
 * It does NOT happen automatically when:
 * - The value object is mutated in place without state update
 *
 * @example
 * ```tsx
 * function OptionButton({ option, isStreaming }) {
 *   // Suspend until option has id and label
 *   useStreamingSuspense(option, isStreaming, requiredKeys(['id', 'label']));
 *   return <button>{option.label}</button>;
 * }
 *
 * // Parent wraps with Suspense
 * <Suspense fallback={<OptionSkeleton />}>
 *   <OptionButton option={option} isStreaming={isStreaming} />
 * </Suspense>
 * ```
 */
import { createContext, type ReactNode, use, useState } from 'react';

import type { StreamingCondition } from '#plugin-chat/lib/ui/streaming-condition.ts';

interface PromiseEntry {
  promise: Promise<void>;
  resolve: () => void;
  resolved: boolean;
}

/**
 * Registry that maintains promises per object+condition combination.
 * Different conditions on the same object get independent promises.
 */
class PromiseRegistry {
  private promises = new Map<string, PromiseEntry>();
  private objectIds = new WeakMap<object, number>();
  private subscribers = new Map<string, Set<symbol>>();
  private nextId = 0;

  private getObjectId(obj: object): number {
    const existing = this.objectIds.get(obj);
    if (existing !== undefined) {
      return existing;
    }

    const id = this.nextId++;
    this.objectIds.set(obj, id);
    return id;
  }

  private getCompositeKey(obj: object, condition: StreamingCondition | undefined): string {
    return `${this.getObjectId(obj)}:${condition?.key ?? 'none'}`;
  }

  /**
   * Get or create a promise for an object+condition combination.
   * Returns the same promise for the same object+condition (stable identity).
   */
  getPromise(obj: object, condition: StreamingCondition | undefined): PromiseEntry {
    const key = this.getCompositeKey(obj, condition);
    const existing = this.promises.get(key);
    if (existing) {
      return existing;
    }

    let resolveRef = () => {
      // Placeholder until promise executor assigns the real resolver
    };
    const promise = new Promise<void>((r) => {
      resolveRef = r;
    });
    const entry: PromiseEntry = { promise, resolve: resolveRef, resolved: false };
    this.promises.set(key, entry);
    return entry;
  }

  /**
   * Resolve the promise for an object+condition (idempotent).
   */
  resolve(obj: object, condition: StreamingCondition | undefined): void {
    const key = this.getCompositeKey(obj, condition);
    const entry = this.promises.get(key);
    if (entry && !entry.resolved) {
      entry.resolved = true;
      entry.resolve();
    }
  }

  /**
   * Subscribe to a promise (call in useEffect).
   * Returns a subscriber ID for cleanup.
   */
  subscribe(obj: object, condition: StreamingCondition | undefined): symbol {
    const key = this.getCompositeKey(obj, condition);
    const subs = this.subscribers.get(key) ?? new Set();
    if (!this.subscribers.has(key)) {
      this.subscribers.set(key, subs);
    }
    const subscriberId = Symbol('subscriber');
    subs.add(subscriberId);
    return subscriberId;
  }

  /**
   * Unsubscribe from a promise (call in cleanup).
   * Resolves promise if no subscribers remain.
   */
  unsubscribe(obj: object, condition: StreamingCondition | undefined, subscriberId: symbol): void {
    const key = this.getCompositeKey(obj, condition);
    const subs = this.subscribers.get(key);
    if (!subs) {
      return;
    }

    subs.delete(subscriberId);

    // If no more subscribers, clean up the promise
    if (subs.size === 0) {
      this.resolve(obj, condition);
      this.promises.delete(key);
      this.subscribers.delete(key);
    }
  }

  /**
   * Check if an object+condition has a pending (unresolved) promise.
   */
  hasPending(obj: object, condition: StreamingCondition | undefined): boolean {
    const key = this.getCompositeKey(obj, condition);
    const entry = this.promises.get(key);
    return entry !== undefined && !entry.resolved;
  }
}

// Global registry - promises are tied to object+condition identity
const globalRegistry = new PromiseRegistry();

/**
 * Context for scoped promise registries.
 * Allows different streaming sessions to be isolated.
 */
const PromiseRegistryContext = createContext<PromiseRegistry | null>(null);

export interface StreamingSuspenseProviderProps {
  children: ReactNode;
}

/**
 * Provider that creates a scoped promise registry for streaming suspense.
 * Use this to isolate streaming sessions (e.g., different chat threads).
 * If not provided, a global registry is used.
 */
export function StreamingSuspenseProvider({ children }: StreamingSuspenseProviderProps) {
  const [registry] = useState(() => new PromiseRegistry());

  return <PromiseRegistryContext value={registry}>{children}</PromiseRegistryContext>;
}

/**
 * Get the current registry (scoped or global fallback).
 */
function _useRegistry(): PromiseRegistry {
  const contextRegistry = use(PromiseRegistryContext);
  return contextRegistry ?? globalRegistry;
}

/**
 * Check if a value meets the condition (no condition = always ready).
 */
function _meetsCondition(value: unknown, condition: StreamingCondition | undefined, isStreaming: boolean): boolean {
  return condition === undefined || condition.isMet(value, isStreaming);
}

/**
 * Suspend the component while streaming if the condition is not met.
 *
 * @param value - The streaming value to check
 * @param isStreaming - Whether streaming is active
 * @param condition - When to consider data "ready" ({@link StreamingCondition});
 *   `undefined` means always ready (never suspends)
 *
 * @example
 * ```tsx
 * // Suspend until id and label exist
 * useStreamingSuspense(option, isStreaming, requiredKeys(['id', 'label']));
 *
 * // Suspend until custom condition
 * useStreamingSuspense(option, isStreaming, {
 *   key: 'label-min-3',
 *   isMet: (opt) => (opt as Option).label?.length > 3,
 * });
 *
 * // Never suspend (just mark as streaming-aware)
 * useStreamingSuspense(option, isStreaming);
 * ```
 */
// biome-ignore lint/style/useComponentExportOnlyModules: Hook is the primary export of this module alongside the Provider component
export function useStreamingSuspense(_value: unknown, _isStreaming: boolean, _condition?: StreamingCondition): void {
  // const registry = useRegistry();
  //
  // // Cleanup on unmount
  // useEffect(() => {
  //   if (!isPlainObject(value) || !isStreaming) {
  //     return;
  //   }
  //
  //   const subscriberId = registry.subscribe(value, condition);
  //   return () => registry.unsubscribe(value, condition, subscriberId);
  // }, [registry, value, condition, isStreaming]);
  //
  // // Non-objects can't have promises attached
  // if (!isPlainObject(value)) {
  //   return;
  // }
  //
  // const isReady = meetsCondition(value, condition, isStreaming);
  //
  // if (isReady || !isStreaming) {
  //   // Condition met or streaming ended - resolve any pending promise
  //   registry.resolve(value, condition);
  //   return;
  // }
  //
  // // Still streaming and condition not met - suspend
  // const { promise } = registry.getPromise(value, condition);
  // throw promise;
}
